import { DependencyContainer } from "./DependencyContainer"

type Lock = {
  Release: () => void
}

type WaitingPromise = {
  Resolve: (lock: Lock) => void
  Reject: (err?: Error) => void
}

export class AsyncSemaphore {

  private _running = 0
  private _waiting: WaitingPromise[] = []

  constructor(public initialCount: number) {
    if (initialCount < 1) {
      throw new Error(
        `The semaphore was created with a max value of ${initialCount} but the max value cannot be less than 1`,
      )
    }
  }

  private Take = () => {
    if (this._waiting.length > 0 && this._running < this.initialCount) {
      this._running++
      const task = this._waiting.shift()
      task?.Resolve({ Release: this.Release })
    }
  }

  public IsLocked = (): boolean => {
    return this._running > 0;
  }

  /**
  * Acquire a lock on the target resource.
  * @returns a function to release the lock, it is critical that this function is called when the task is finished with the resource.
  */
  public AcquireAsync = (): Promise<Lock> => {
    if (this._running < this.initialCount) {
      this._running++
      return Promise.resolve({ Release: this.Release })
    }

    return new Promise<Lock>((resolve, reject) => {
      this._waiting.push({ Resolve: resolve, Reject: reject })
    })
  }

  public Release = () => {
    if (this._running === 0) {
      throw new Error('Release called more times than AcquireAsync');
    }
    this._running--
    this.Take()
  }

  /**
   * Purge all waiting tasks from the Semaphore instance
   */
  public Purge = () => {
    this._waiting.forEach(task => {
      task.Reject(
        new Error('The semaphore was purged and as a result this task has been cancelled'),
      )
    })
    this._running = 0
    this._waiting = []
  }
}